import { IAvailabilityForMinMaxLive, IMappedProductInfo, IPlan, IPlanAgeFaceAmountRange, IProductInfo, IProductVariant, JsonData, JuvenileAdultMinMax, MinMaxExtendedValues, MinMaxValues, PlanKind, PlanType } from "../data/productInfoInterfaces";
import { Language } from "../utils/constants";
import { IllustrationProduct } from "@/common/data/illustrationInterface";
import { getProducts, redirectToErrorPage } from "@/common/utils/api";
import { isNumberValue, sortByDisplayOrder } from "@/common/utils/helpers";
import { EqOptionsFields } from "@equitable/equx";
import React, { createContext, useContext, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ProductInfoConstants {
  maxNumberOfRidersForInsured: number;
  cirMinDifferenceBetweenInsuredAgeAndUptoAgeValue: number;
  codeAdditionalAccidentalDeathBenefitRider: string;
  codeCriticalIllnessRider: string;
  codesWaiverOfPremium: string[];
  codesRatingImpactedRiders: string[];
  ciBasePlanFaceAmountThreshold: number;
  juvenileMaxAge: number;
  codesTermCoverages: string[];
  defaultRatingPercentageId: number;
  declinedRatingPercentageId: number;
  defaultRatingDurationYear: number;
  ratingAmountValue: MinMaxValues;
  ratingDurationYear: MinMaxValues | MinMaxExtendedValues;

}

export interface IProductInfoContext {
  load: (code: IllustrationProduct) => void;
  getProductVariant: (numberOfInsuredsOnPolicy: number, isJoint: boolean) => IProductVariant | undefined;
  getAvailablePlans: (isRider: boolean, numberOfInsuredsOnPolicy: number, isJoint: boolean) => IPlan[];
  isPlanAvailableForInsured: (
    productVariant: IProductVariant,
    planId: number,
    indexOfInsured: number,
    age: number | null,
    ignoreAge?: boolean
  ) => boolean;
  getJsonData: <T, >(obj: JsonData) => T;
  getRatingPercentagesOptions: (planId: number | null | undefined) => EqOptionsFields[];
  data: IProductInfo;
  mappedData: IMappedProductInfo;
  constants: ProductInfoConstants;
  juvenileAdultMinMax: JuvenileAdultMinMax;
}

type TranslatedName = Record<"name" | "nameEn" | "nameFr" | "shortName" | "shortNameEn" | "shortNameFr", string>;

const ProductInfoProvider = ({ children }: {children: React.ReactNode }) => {

  const [productInfo, setProductInfo] = useState<IProductInfo>();
  const [mappedProductInfo, setMappedProductInfo] = useState<IMappedProductInfo>();
  const { i18n } = useTranslation();
  const isLoading = useRef<boolean>(false);
  const [juvenileAdultMinMax, setJuvenileAdultMinMax] = useState<JuvenileAdultMinMax>(undefined!);

  const getJsonData = <T, >(obj: JsonData | undefined): T => (obj?.jsonData ?? undefined) as T;

  const constants: ProductInfoConstants = getJsonData<ProductInfoConstants>(productInfo);

  const load = (code: IllustrationProduct) => {
    if (isLoading.current) {
      return;
    }

    getProducts(code).then((prodInfo: IProductInfo) => {
      setProductInfo(setLanguage(prodInfo));
      initialize(prodInfo);
    })
      .catch(() => redirectToErrorPage())
      .finally(() => isLoading.current = false);
  };

  const setLanguage = (productInfo: IProductInfo): IProductInfo => {
    const prodInfo = { ...productInfo! };
    setCurrentLanguage(prodInfo as unknown as TranslatedName);
    [prodInfo?.plans,
      prodInfo?.productVariants,
      prodInfo.criticalIllnessRiderPremiumTypes,
      prodInfo.ratingPercentages
    ]?.forEach(a => (a as unknown as TranslatedName[])?.forEach(setCurrentLanguage));

    return prodInfo;
  };

  const setCurrentLanguage = (obj: TranslatedName) => {
    obj.name = i18n.language === Language.fr ? obj.nameFr : obj.nameEn;

    if (obj.shortNameEn?.length > 0) {
      obj.shortName = i18n.language === Language.fr ? obj.shortNameFr : obj.shortNameEn;
    }
  };

  useLayoutEffect(() => {
    if (productInfo == null) {
      return;
    }

    const prodInfo = { ...productInfo };
    setProductInfo(setLanguage(prodInfo));
    initialize(prodInfo);
  }, [i18n.language]);

  const initialize = (prodInfo: IProductInfo) => {
    // mapping data
    const mData: IMappedProductInfo = {
      productVariants: {},
      plans: {},
      planMinMaxLives: {},
      planAgeFaceAmountRanges: {},
      criticalIllnessRiderPremiumTypes: {}
    };

    // productVariants[productVariantId] ==> productVariant
    prodInfo.productVariants.map((a) => (mData.productVariants[a.id] = a));

    // plans[planId] ==> plan
    prodInfo.plans.map((a) => (mData.plans[a.id] = a));

    // plan<MinMaxLives>[planId][productVariantId] ==> IAvailabilityForMinMaxLive
    prodInfo.plans.map((plan) => {
      mData.planMinMaxLives[plan.id] = {};
      const obj = mData.planMinMaxLives[plan.id];
      plan.availabilitiesForMinMaxLives.map((a) => (obj[a.productVariantId] = a));
    });

    // planAgeFaceAmountRanges[planId][productVariantId] ==> IPlanAgeFaceAmountRange[]
    prodInfo.plans.map((plan) => {
      mData.planAgeFaceAmountRanges[plan.id] = {};
      const obj = mData.planAgeFaceAmountRanges[plan.id];
      plan.planAgeFaceAmountRanges.map((a) => (obj[a.productVariantId] = plan.planAgeFaceAmountRanges));
    });

    prodInfo.criticalIllnessRiderPremiumTypes.map((a) => (mData.criticalIllnessRiderPremiumTypes[a.id] = a));

    setMappedProductInfo(mData);
    setJuvenileAdultMinMax(getJuvenileAdultMinMax(prodInfo));
  };

  const getProductVariant = (numberOfInsuredsOnPolicy: number, isJoint: boolean): IProductVariant | undefined => {
    const result: IProductVariant | undefined =
      productInfo?.productVariants.find((a) =>
        numberOfInsuredsOnPolicy >= a.minNumberOfLives &&
        numberOfInsuredsOnPolicy <= a.maxNumberOfLives &&
        a.isJoint === isJoint);

    return result;
  };

  const getAvailablePlans = (isRider: boolean, numberOfInsuredsOnPolicy: number, isJoint: boolean): IPlan[] => {
    const validProductVariant = getProductVariant(numberOfInsuredsOnPolicy, isJoint);

    const availablePlans = validProductVariant !== undefined
      ? productInfo!.plans.filter((a) =>
          validProductVariant.planIds.some((b) =>
            b === a.id && a.planType === (isRider ? PlanType.Rider : PlanType.BasePlan)))
      : ([] as IPlan[]);

    return availablePlans;
  };

  const isPlanAvailableForInsured = (
    productVariant: IProductVariant,
    planId: number,
    indexOfInsured: number,
    age: number | null,
    ignoreAge: boolean = false
  ): boolean => {
    const policyLive = indexOfInsured + 1; // position insured on policy, 0-based to 1-based
    const planMinMaxLives: IAvailabilityForMinMaxLive | undefined =
      mappedProductInfo?.planMinMaxLives[planId][productVariant.id];
    const planAgeFaceAmountRanges: IPlanAgeFaceAmountRange[] | undefined =
      mappedProductInfo?.planAgeFaceAmountRanges[planId][productVariant.id];
    const result =
      planMinMaxLives != null && // position of the insured on policy in the allowed range
      policyLive >= (planMinMaxLives!.isAvailableForLiveMin ?? 0) &&
      policyLive <= (planMinMaxLives!.isAvailableForLiveMax ?? Number.MAX_VALUE) &&
      (
        ignoreAge ||
        (
          isNumberValue(age) && // age is in allowed range
          age >= productVariant.minAge &&
          age <= productVariant.maxAge &&
          planAgeFaceAmountRanges != null &&
          planAgeFaceAmountRanges!.some((a) =>
            (age >= a.minAge && age <= a.maxAge) ||
            (a.minAge === 0 && a.maxAge === 0))
        )
      );

    return result;
  };

  const getJuvenileAdultMinMax = (prodInfo: IProductInfo): JuvenileAdultMinMax => {

    const plans = prodInfo!.plans.filter(a =>
      a.planKind === PlanKind.Insured &&
      a.planType === PlanType.BasePlan
    ) || [];

    const juvenile = plans
      .flatMap(a => a.planAgeFaceAmountRanges.filter(b =>
        prodInfo!.adultAgeThreshold > b.minAge && prodInfo!.adultAgeThreshold > b.maxAge)
      )
      .filter(a => a != null);

    const adult = plans
      .flatMap(a => a.planAgeFaceAmountRanges.filter(b =>
        prodInfo!.adultAgeThreshold <= b.minAge && prodInfo!.adultAgeThreshold <= b.minAge)
      )
      .filter(a => a != null);

    const result: JuvenileAdultMinMax = {
      juvenileMin: Math.min(...juvenile.map(a => a?.minFaceAmount || 0)),
      juvenileMax: Math.max(...juvenile.map(a => a?.maxFaceAmount || 0)),
      adultMin: Math.min(...adult.map(a => a?.minFaceAmount || 0)),
      adultMax: Math.max(...adult.map(a => a?.maxFaceAmount || 0))
    };

    return result;
  };

  const getRatingPercentagesOptions = (planId: number | null | undefined): EqOptionsFields[] => {

    if (productInfo == null) {
      return [];
    }

    const availableRatingIds =
      productInfo.ratingPercentagesMemberships
        .filter((rpm) => rpm.planId === planId)
        .map((rpm) => rpm.percentageId);

    const ratingPercentageValues =
      productInfo.ratingPercentages
        .filter((rating) => availableRatingIds.includes(rating.id))
        .sort(sortByDisplayOrder)
        .map((rating) => ({
          id: rating.id,
          label: rating.name,
          value: rating.id
        }));

    return ratingPercentageValues;
  };

  return (
    <ProductInfoContext.Provider
      value={{
        load,
        getProductVariant,
        getAvailablePlans,
        isPlanAvailableForInsured,
        getJsonData,
        getRatingPercentagesOptions,
        data: productInfo!,
        mappedData: mappedProductInfo!,
        constants,
        juvenileAdultMinMax
      }}>
      {children}
    </ProductInfoContext.Provider>
  );
};

export const ProductInfoContext = createContext<IProductInfoContext>(undefined!);
export const useProductInfo = () => useContext(ProductInfoContext);
export default ProductInfoProvider;
